import { useState, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { aiApi } from '../../api/ai';
import toast from 'react-hot-toast';
import { Sparkles, Loader2, X, RefreshCw } from 'lucide-react';

interface ReplyAIAssistProps {
  ticketId: string;
  onApply: (text: string) => void;
}

export default function ReplyAIAssist({ ticketId, onApply }: ReplyAIAssistProps) {
  const [suggested, setSuggested] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const suggestMutation = useMutation({
    mutationFn: () => aiApi.suggestReply(ticketId),
    onSuccess: (text) => {
      setSuggested(text);
      setOpen(true);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  function handleUse() {
    if (suggested) onApply(suggested);
    setOpen(false);
    setSuggested(null);
  }

  function handleDismiss() {
    setOpen(false);
    setSuggested(null);
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        className="btn btn-ghost text-xs inline-flex items-center gap-1.5 text-primary"
        onClick={() => {
          if (suggested) {
            setOpen(v => !v);
          } else {
            suggestMutation.mutate();
          }
        }}
        disabled={suggestMutation.isPending}
      >
        {suggestMutation.isPending ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Sparkles className="w-3.5 h-3.5" />
        )}
        {suggestMutation.isPending ? 'Generating…' : 'AI suggest reply'}
      </button>

      {open && suggested && (
        <div
          ref={popoverRef}
          className="absolute bottom-full mb-2 left-0 z-50 w-96 max-w-[calc(100vw-2rem)] rounded-xl border border-line bg-surface shadow-xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-line-light">
            <div className="flex items-center gap-2 text-sm font-medium text-fg">
              <Sparkles className="w-4 h-4 text-primary" />
              AI suggested reply
            </div>
            <button
              type="button"
              onClick={handleDismiss}
              className="text-muted hover:text-fg"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="px-4 py-3 max-h-60 overflow-y-auto">
            <p className="text-sm text-muted whitespace-pre-wrap leading-relaxed">
              {suggested}
            </p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-line-light">
            <button
              type="button"
              className="btn btn-ghost text-xs inline-flex items-center gap-1.5"
              onClick={() => suggestMutation.mutate()}
              disabled={suggestMutation.isPending}
            >
              {suggestMutation.isPending ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3" />
              )}
              Regenerate
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn btn-ghost text-xs"
                onClick={handleDismiss}
              >
                Dismiss
              </button>
              <button
                type="button"
                className="btn btn-primary text-xs"
                onClick={handleUse}
              >
                Use reply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
